import { DollarOutlined, LockOutlined, UnlockOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Empty,
  Flex,
  Form,
  Input,
  Radio,
  Statistic,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ActionDrawer } from '../../components/ActionDrawer';
import { PrintButton } from '../../components/print/PrintButton';
import { BookingsToReceive } from './BookingsToReceive';
import { RefundsToPay } from './RefundsToPay';
import { LoadMore } from '../../components/LoadMore';
import { useCursorList } from '../../lib/use-cursor-list';
import { ApiError } from '../../lib/api-client';
import { invalidateAfterInvoiceMoney } from './invalidate-invoice-money';
import { formatDateTime, orDash } from '../../lib/format';
import { formatMoney } from '../../lib/money';
import {
  closeSession,
  invoiceFromCounterOrder,
  listAwaitingCounterOrders,
  listInvoices,
  mySession,
  openSession,
  takePayment,
  type AwaitingCounterOrder,
  type Invoice,
  type Payment,
  type PaymentMethod,
} from './api';

const { Text, Title } = Typography;

const METHODS: PaymentMethod[] = ['CASH', 'CARD', 'BANK_TRANSFER', 'EWALLET'];

/**
 * The till.
 *
 * Three decisions worth knowing about:
 *
 *  - **Cash needs an open drawer**, and the screen leads with that rather than
 *    letting a cashier discover it at the moment a customer is holding out
 *    notes.
 *  - **The idempotency key is generated ONCE per payment attempt**, when the
 *    drawer opens, not per click. A key regenerated on each render would make
 *    the guard useless — the point is that a double-tap sends the SAME key
 *    (CLAUDE.md §9).
 *  - **Closing states the variance before it is committed.** Counting is the
 *    moment a shortfall becomes real, and a number somebody sees before
 *    confirming is a number they will check.
 */
export function TillPage(): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [opening, setOpening] = useState(false);
  const [closing, setClosing] = useState(false);
  const [float, setFloat] = useState('200.00');
  const [counted, setCounted] = useState('');
  const [varianceNote, setVarianceNote] = useState('');
  const [paying, setPaying] = useState<Invoice | null>(null);
  const [method, setMethod] = useState<PaymentMethod>('CASH');
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');
  const [paymentKey, setPaymentKey] = useState('');
  // Set once the payment is taken: the drawer stays open as a done state.
  const [taken, setTaken] = useState<Payment | null>(null);

  const { data: session, error: sessionError } = useQuery({
    queryKey: ['till-session'],
    queryFn: mySession,
    retry: (count, e) => !(e instanceof ApiError && e.status === 403) && count < 3,
  });
  // Accounts can pay refunds but holds no drawer: show no drawer card at all.
  const noDrawerRole = sessionError instanceof ApiError && sessionError.status === 403;
  const unpaid = useCursorList(['invoices', 'unpaid'], (page) => listInvoices({ ...page, unpaid: true }));
  const awaiting = useCursorList(['counter-orders', 'awaiting'], listAwaitingCounterOrders);

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['till-session'] });
    void queryClient.invalidateQueries({ queryKey: ['invoices'] });
  };
  const fail = (e: unknown, fallback: string): void => {
    void message.error(e instanceof ApiError ? e.message : fallback);
  };

  const open = useMutation({
    mutationFn: () => openSession({ openingFloat: float.trim() }),
    onSuccess: () => {
      refresh();
      setOpening(false);
    },
    onError: (e: unknown) => {
      fail(e, t('till.openFailed'));
    },
  });

  const close = useMutation({
    mutationFn: () =>
      closeSession({
        countedCash: counted.trim(),
        ...(varianceNote.trim().length > 0 ? { varianceNote: varianceNote.trim() } : {}),
      }),
    onSuccess: (result) => {
      refresh();
      setClosing(false);
      void message.success(
        result.variance === '0'
          ? t('till.closedExact')
          : t('till.closedWithVariance', { variance: formatMoney(result.variance ?? '0') }),
      );
    },
    // The API refuses an unexplained variance and says how far out it is.
    onError: (e: unknown) => {
      fail(e, t('till.closeFailed'));
    },
  });

  const openUid = useRef<string | null>(null);
  useEffect(() => {
    openUid.current = paying?.uid ?? null;
  }, [paying]);

  const pay = useMutation({
    mutationFn: (target: Invoice) =>
      takePayment(
        target.uid,
        {
          method,
          amount: amount.trim(),
          ...(reference.trim().length > 0 ? { reference: reference.trim() } : {}),
        },
        paymentKey,
      ),
    onSuccess: (result, target) => {
      refresh();
      invalidateAfterInvoiceMoney(queryClient, target);
      // The drawer stays open and turns into a done state. The key stays too,
      // so nothing on screen can pay twice before Done. Only if it is still
      // this invoice's drawer: a late result must not land on another's.
      if (openUid.current === target.uid) setTaken(result);
    },
    onError: (e: unknown) => {
      fail(e, t('till.payFailed'));
    },
  });

  const closePaying = (): void => {
    setPaying(null);
    setTaken(null);
  };

  const startPayment = (invoice: Invoice): void => {
    setTaken(null);
    setMethod('CASH');
    setAmount(invoice.balanceDue);
    setReference('');
    // Generated ONCE, here. A key regenerated per click would make the guard
    // useless — the whole point is that a double-tap sends the same one.
    setPaymentKey(crypto.randomUUID());
    setPaying(invoice);
  };

  // Billing hands straight to the payment drawer: the customer is standing at
  // the till, so bill-then-pay is one motion, not two screens.
  const bill = useMutation({
    mutationFn: (order: AwaitingCounterOrder) => invoiceFromCounterOrder(order.uid),
    onSuccess: (invoice) => {
      void queryClient.invalidateQueries({ queryKey: ['counter-orders'] });
      refresh();
      startPayment(invoice);
    },
    onError: (e: unknown) => {
      fail(e, t('till.billFailed'));
    },
  });

  const expected =
    session === null || session === undefined
      ? null
      : Number(session.openingFloat) +
        (session.takings.find((k) => k.method === 'CASH')?.total === undefined
          ? 0
          : Number(session.takings.find((k) => k.method === 'CASH')?.total ?? '0'));

  const variancePreview =
    expected === null || counted.trim().length === 0 ? null : Number(counted) - expected;

  return (
    <Flex vertical gap={16}>
      {noDrawerRole ? null : session === null || session === undefined ? (
        <Card>
          <Flex vertical gap={12} align="flex-start">
            <Alert
              type="warning"
              showIcon
              style={{ width: '100%' }}
              message={t('till.noDrawer')}
              description={t('till.noDrawerHelp')}
            />
            <Button
              type="primary"
              size="large"
              icon={<UnlockOutlined />}
              onClick={() => {
                setFloat('200.00');
                setOpening(true);
              }}
            >
              {t('till.open')}
            </Button>
          </Flex>
        </Card>
      ) : (
        <Card
          title={t('till.drawerOpen', { since: formatDateTime(session.openedAt) })}
          extra={
            <Button
              icon={<LockOutlined />}
              size="large"
              onClick={() => {
                setCounted('');
                setVarianceNote('');
                setClosing(true);
              }}
            >
              {t('till.close')}
            </Button>
          }
        >
          <Flex gap={32} wrap>
            <Statistic title={t('till.float')} value={formatMoney(session.openingFloat)} />
            {session.takings.map((taking) => (
              <Statistic
                key={taking.method}
                title={t(`till.method.${taking.method}`)}
                value={formatMoney(taking.total)}
                suffix={
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    {t('till.txnCount', { count: taking.count })}
                  </Text>
                }
              />
            ))}
            {session.takings.length === 0 && <Text type="secondary">{t('till.nothingYet')}</Text>}
          </Flex>
        </Card>
      )}

      <BookingsToReceive drawerOpen={session !== null && session !== undefined} />
      <RefundsToPay drawerOpen={session !== null && session !== undefined} />

      {(awaiting.rows.length > 0 || awaiting.isFetching) && (
        <Card title={t('till.counterOrders')} styles={{ body: { padding: 0 } }}>
          <Table<AwaitingCounterOrder>
            dataSource={awaiting.rows}
            rowKey={(row) => row.uid}
            loading={awaiting.isFetching}
            pagination={false}
            size="middle"
            scroll={{ x: 'max-content' }}
            locale={{
              emptyText: (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('till.noCounterOrders')} />
              ),
            }}
            columns={[
              {
                title: t('till.fields.orderNo'),
                dataIndex: 'orderNo',
                width: 175,
                render: (value: string) => <span className="tabular">{value}</span>,
              },
              {
                title: t('sales.fields.customer'),
                dataIndex: 'customerName',
                render: (value: string | null) => value ?? t('till.walkIn'),
              },
              {
                title: t('till.fields.total'),
                dataIndex: 'grandTotal',
                width: 140,
                align: 'right' as const,
                render: (value: string) => formatMoney(value),
              },
              {
                title: '',
                width: 140,
                render: (_: unknown, row: AwaitingCounterOrder) => (
                  <Button
                    type="primary"
                    icon={<DollarOutlined />}
                    loading={bill.isPending && bill.variables.uid === row.uid}
                    disabled={bill.isPending}
                    onClick={() => {
                      bill.mutate(row);
                    }}
                  >
                    {t('till.billOrder')}
                  </Button>
                ),
              },
            ]}
          />
          <LoadMore list={awaiting} />
        </Card>
      )}

      <Card title={t('till.unpaid')} styles={{ body: { padding: 0 } }}>
        <Table<Invoice>
          dataSource={unpaid.rows}
          rowKey={(row) => row.uid}
          loading={unpaid.isFetching}
          pagination={false}
          size="middle"
          scroll={{ x: 'max-content' }}
          locale={{
            emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('till.allSettled')} />,
          }}
          columns={[
            {
              title: t('till.fields.invoiceNo'),
              dataIndex: 'invoiceNo',
              width: 175,
              render: (value: string) => <span className="tabular">{value}</span>,
            },
            {
              title: t('sales.fields.customer'),
              dataIndex: 'customerName',
              render: (value: string | null) => value ?? t('till.walkIn'),
            },
            {
              title: t('vehicles.fields.plateNo'),
              dataIndex: 'plateNo',
              width: 130,
              render: (value: string | null) => orDash(value),
            },
            {
              title: t('till.fields.total'),
              dataIndex: 'grandTotal',
              width: 140,
              align: 'right' as const,
              render: (value: string) => formatMoney(value),
            },
            {
              title: t('till.fields.due'),
              dataIndex: 'balanceDue',
              width: 150,
              align: 'right' as const,
              render: (value: string, row: Invoice) => (
                <Flex vertical align="end" gap={2}>
                  <Text strong>{formatMoney(value)}</Text>
                  {row.status === 'PARTIALLY_PAID' && (
                    <Tag color="warning" style={{ marginInlineEnd: 0 }}>
                      {t('till.partPaid')}
                    </Tag>
                  )}
                </Flex>
              ),
            },
            {
              title: '',
              width: 160,
              render: (_: unknown, row: Invoice) => (
                <Flex gap={8}>
                  <Button
                    type="primary"
                    icon={<DollarOutlined />}
                    onClick={() => {
                      startPayment(row);
                    }}
                  >
                    {t('till.take')}
                  </Button>
                  <Button
                    onClick={() => {
                      void navigate(`/pos/invoices/${row.uid}`);
                    }}
                  >
                    {t('till.view')}
                  </Button>
                </Flex>
              ),
            },
          ]}
        />
        <LoadMore list={unpaid} />
      </Card>

      <ActionDrawer
        open={opening}
        title={t('till.open')}
        submitLabel={t('till.open')}
        submitting={open.isPending}
        onClose={() => {
          setOpening(false);
        }}
        onSubmit={() => {
          open.mutate();
        }}
      >
        <Form layout="vertical">
          <Form.Item label={t('till.fields.float')} required help={t('till.floatHelp')}>
            <Input
              inputMode="decimal"
              prefix="MYR"
              size="large"
              value={float}
              onChange={(e) => {
                setFloat(e.target.value);
              }}
            />
          </Form.Item>
        </Form>
      </ActionDrawer>

      <ActionDrawer
        open={closing}
        title={t('till.close')}
        submitLabel={t('till.close')}
        submitting={close.isPending}
        submitDisabled={
          counted.trim().length === 0 ||
          (variancePreview !== null && variancePreview !== 0 && varianceNote.trim().length < 3)
        }
        onClose={() => {
          setClosing(false);
        }}
        onSubmit={() => {
          close.mutate();
        }}
      >
        <Form layout="vertical">
          <Descriptions column={1} bordered size="small" style={{ marginBottom: 16 }}>
            <Descriptions.Item label={t('till.fields.expected')}>
              <Text strong>{expected === null ? '—' : formatMoney(String(expected))}</Text>
            </Descriptions.Item>
          </Descriptions>

          <Form.Item label={t('till.fields.counted')} required help={t('till.countedHelp')}>
            <Input
              inputMode="decimal"
              prefix="MYR"
              size="large"
              value={counted}
              onChange={(e) => {
                setCounted(e.target.value);
              }}
            />
          </Form.Item>

          {variancePreview !== null && (
            <Alert
              type={variancePreview === 0 ? 'success' : 'warning'}
              showIcon
              style={{ marginBottom: 16 }}
              // Shown BEFORE committing: counting is the moment a shortfall
              // becomes real, and a number somebody sees first is one they check.
              message={
                variancePreview === 0
                  ? t('till.matches')
                  : t('till.outBy', { amount: formatMoney(String(variancePreview)) })
              }
            />
          )}

          {variancePreview !== null && variancePreview !== 0 && (
            <Form.Item label={t('till.fields.whatHappened')} required>
              <Input.TextArea
                rows={3}
                value={varianceNote}
                onChange={(e) => {
                  setVarianceNote(e.target.value);
                }}
                placeholder={t('till.variancePlaceholder')}
              />
            </Form.Item>
          )}
        </Form>
      </ActionDrawer>

      <ActionDrawer
        open={paying !== null}
        title={t('till.takeFrom', { name: paying?.customerName ?? t('till.walkIn') })}
        submitLabel={t('till.take')}
        submitting={pay.isPending}
        submitDisabled={amount.trim().length === 0}
        onClose={closePaying}
        onSubmit={() => {
          if (paying !== null) pay.mutate(paying);
        }}
        footer={
          taken === null ? undefined : (
            <Flex justify="flex-end">
              <Button type="primary" size="large" onClick={closePaying}>
                {t('till.done')}
              </Button>
            </Flex>
          )
        }
        consequence={
          taken !== null
            ? undefined
            : t('till.payConsequence', {
                amount: formatMoney(amount.trim().length > 0 ? amount.trim() : '0'),
                invoiceNo: paying?.invoiceNo ?? '',
              })
        }
      >
        {taken !== null ? (
          <Flex vertical gap={16}>
            <Alert type="success" showIcon message={t('till.doneTitle', { receiptNo: taken.receiptNo })} />
            <PrintButton doc="receipt" uid={taken.uid} label={t('print.printReceipt')} type="primary" />
            {taken.invoiceStatus === 'PAID' && paying?.source === 'COUNTER' && (
              <PrintButton doc="invoice" uid={paying.uid} label={t('print.printInvoice')} />
            )}
          </Flex>
        ) : (
          <Form layout="vertical">
            <Card size="small" style={{ marginBottom: 16 }}>
              <Flex justify="space-between" align="center">
                <Text type="secondary">{t('till.fields.due')}</Text>
                <Title level={3} style={{ margin: 0 }}>
                  {formatMoney(paying?.balanceDue ?? '0')}
                </Title>
              </Flex>
            </Card>

            <Form.Item label={t('till.fields.method')} required>
              <Radio.Group
                value={method}
                onChange={(e) => {
                  setMethod(e.target.value as PaymentMethod);
                }}
                size="large"
                buttonStyle="solid"
                optionType="button"
                options={METHODS.map((m) => ({ value: m, label: t(`till.method.${m}`) }))}
              />
            </Form.Item>

            {method === 'CASH' && (session === null || session === undefined) && (
              <Alert
                type="error"
                showIcon
                style={{ marginBottom: 16 }}
                message={t('till.noDrawer')}
                description={t('till.noDrawerHelp')}
              />
            )}

            <Form.Item label={t('till.fields.amount')} required help={t('till.amountHelp')}>
              <Input
                inputMode="decimal"
                prefix="MYR"
                size="large"
                value={amount}
                onChange={(e) => {
                  setAmount(e.target.value);
                }}
              />
            </Form.Item>

            {method !== 'CASH' && (
              <Form.Item label={t('till.fields.reference')} help={t('till.referenceHelp')}>
                <Input
                  size="large"
                  value={reference}
                  onChange={(e) => {
                    setReference(e.target.value);
                  }}
                />
              </Form.Item>
            )}
          </Form>
        )}
      </ActionDrawer>
    </Flex>
  );
}
